import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FaPaperclip,
  FaTimes,
  FaRegSmile,
  FaSearch,
  FaTelegramPlane,
  FaEllipsisV,
  FaUsers,
  FaPlus,
  FaBullhorn,
  FaComments,
} from 'react-icons/fa';
import axios from 'axios';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import EmojiPicker from 'emoji-picker-react';
import { useSearchParams } from 'react-router-dom';
import { createChatSocket } from '../../../utils/chatSocket';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import {
  getChatActivityMap,
  getUnreadCounts,
  markAnnouncementsAsRead,
  markContactAsRead,
  pushAnnouncementNotification,
  pushChatNotification,
  removeChatNotificationsForConversation,
  setActiveChatContact,
  setChatActivity,
} from '../../../utils/chatNotifications';
import '../../../styles/ChatWorkspaceDummy.css';

const ANNOUNCEMENT_STORAGE_KEY = 'adminChatAnnouncements';

const readAnnouncements = () => {
  try {
    const raw = localStorage.getItem(ANNOUNCEMENT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeAnnouncements = (rows) => {
  localStorage.setItem(
    ANNOUNCEMENT_STORAGE_KEY,
    JSON.stringify(rows.slice(0, 25)),
  );
};

const mapServerAnnouncement = (row) => {
  const eventId = row?.eventId || row?.id || '';
  return {
    id: eventId || `announcement-${Date.now()}`,
    eventId: eventId || undefined,
    title: row?.title || 'Announcement',
    message: row?.message || '',
    senderName: row?.senderName || 'Announcement',
    createdAt: row?.createdAt || new Date().toISOString(),
  };
};

const mergeAnnouncementLists = (serverRows, localRows) => {
  const byKey = new Map();
  [...(localRows || []), ...(serverRows || [])].forEach((item) => {
    const key = item?.eventId || item?.id;
    if (!key) return;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, item);
      return;
    }
    const existingTs = new Date(existing.createdAt || 0).getTime();
    const nextTs = new Date(item.createdAt || 0).getTime();
    if (nextTs >= existingTs) byKey.set(key, { ...existing, ...item });
  });
  return Array.from(byKey.values())
    .sort(
      (a, b) =>
        new Date(b.createdAt || 0).getTime() -
        new Date(a.createdAt || 0).getTime(),
    )
    .slice(0, 50);
};

const extractAnnouncementsFromAck = (response) => {
  if (!response || typeof response !== 'object') return [];
  if (Array.isArray(response.announcements)) return response.announcements;
  if (Array.isArray(response?.data?.announcements)) {
    return response.data.announcements;
  }
  if (
    response?.status === 'ok' &&
    Array.isArray(response?.data?.announcements)
  ) {
    return response.data.announcements;
  }
  if (Array.isArray(response)) return response;
  return [];
};

const announcementKey = (item) => item?.eventId || item?.id || '';

const revokeComposerPreviews = (items) => {
  (items || []).forEach((item) => {
    if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
  });
};

const toComposerFileItems = (files) =>
  files.map((file) => ({
    file,
    previewUrl: file.type?.startsWith('image/')
      ? URL.createObjectURL(file)
      : null,
  }));

const buildGroupTitle = (conversation, currentUserId) => {
  const customName = String(conversation?.groupName || '').trim();
  if (customName) return customName;
  const members = Array.isArray(conversation?.members)
    ? conversation.members
    : [];
  const others = members.filter(
    (member) => member?.id && member.id !== currentUserId,
  );
  const labels = others
    .map(
      (member) =>
        `${member.firstName || ''} ${member.lastName || ''}`.trim() ||
        member.email,
    )
    .filter(Boolean);
  if (labels.length === 0) return 'My Group';
  if (labels.length <= 2) return labels.join(', ');
  return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
};

const isManagementRole = (roles = []) => {
  if (!Array.isArray(roles)) return false;
  return roles.some((role) => {
    const normalized = String(role).toLowerCase().replace(/[\s_/-]+/g, '');
    return (
      normalized.includes('admin') ||
      normalized.includes('manager') ||
      normalized.includes('supervisor') ||
      normalized.includes('operationsmanager')
    );
  });
};

const toDisplayRole = (value) => {
  if (!value) return 'User';
  const normalized = String(value)
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return 'User';
  return normalized
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getPrimaryRole = (roles) => {
  if (!Array.isArray(roles) || roles.length === 0) return 'User';
  const roleStrings = roles.map((role) => String(role));
  const normalized = roleStrings.map((role) =>
    role.toLowerCase().replace(/[\s_/-]+/g, ''),
  );

  const priorities = [
    { key: 'superadmin', label: 'Super Admin' },
    { key: 'admin', label: 'Admin' },
    { key: 'operationsmanager', label: 'Operations Manager' },
    { key: 'manager', label: 'Manager' },
    { key: 'supervisor', label: 'Supervisor' },
  ];

  for (const priority of priorities) {
    const index = normalized.findIndex((role) => role.includes(priority.key));
    if (index >= 0) {
      return priority.label;
    }
  }

  return toDisplayRole(roleStrings[0]);
};

const ChatWorkspace = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [leftPanelView, setLeftPanelView] = useState('chats');
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [leftPaneWidth, setLeftPaneWidth] = useState(260);
  const [isResizing, setIsResizing] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [conversationSummaries, setConversationSummaries] = useState([]);
  const [groupConversations, setGroupConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [conversationId, setConversationId] = useState('');
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState('');
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState(-1);
  const [showMentionPicker, setShowMentionPicker] = useState(false);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [status, setStatus] = useState('Connecting...');
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [currentUserRoles, setCurrentUserRoles] = useState([]);
  const [announcements, setAnnouncements] = useState(() => readAnnouncements());
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementMessage, setAnnouncementMessage] = useState('');
  const [isPostingAnnouncement, setIsPostingAnnouncement] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [groupMemberIds, setGroupMemberIds] = useState([]);
  const [pendingGroupMemberIds, setPendingGroupMemberIds] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [isAddingMembers, setIsAddingMembers] = useState(false);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  const [isLeavingGroup, setIsLeavingGroup] = useState(false);
  const [groupConfirmAction, setGroupConfirmAction] = useState(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(260);
  const socketRef = useRef(null);
  const selectedUserRef = useRef(null);
  const selectedGroupRef = useRef(null);
  const leftPanelViewRef = useRef('chats');
  const conversationIdRef = useRef('');
  const currentUserIdRef = useRef('');
  const listRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const messageRefs = useRef({});
  const handledDeepLinkRef = useRef('');
  const token = Cookies.get('accessToken');
  const [highlightedMessageId, setHighlightedMessageId] = useState('');

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  useEffect(() => {
    selectedGroupRef.current = selectedGroup;
  }, [selectedGroup]);

  useEffect(() => {
    leftPanelViewRef.current = leftPanelView;
  }, [leftPanelView]);

  const formatTime = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateDividerLabel = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    const startOfDay = (date) => {
      const x = new Date(date);
      x.setHours(0, 0, 0, 0);
      return x.getTime();
    };
    const diffDays = Math.round(
      (startOfDay(now) - startOfDay(d)) / 86_400_000,
    );
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) {
      return d.toLocaleDateString([], { weekday: 'long' });
    }
    return d.toLocaleDateString([], {
      day: 'numeric',
      month: 'long',
      year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const formatMessageStamp = (value) => {
    if (!value) return '';
    const divider = formatDateDividerLabel(value);
    const time = formatTime(value);
    if (divider === 'Today') return time;
    if (divider === 'Yesterday') return `Yesterday, ${time}`;
    if (/^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)$/u.test(divider)) {
      return `${divider}, ${time}`;
    }
    const d = new Date(value);
    const shortDate = d.toLocaleDateString([], { day: 'numeric', month: 'short' });
    return `${shortDate}, ${time}`;
  };

  const [floatingChatDate, setFloatingChatDate] = useState('');

  const getAttachmentLabel = (url, key, index) => {
    const source = key || url;
    if (!source) return `File ${index + 1}`;
    try {
      const pathname = source.startsWith('http')
        ? new URL(source).pathname
        : source;
      const parts = pathname.split('/');
      return decodeURIComponent(parts[parts.length - 1]) || `File ${index + 1}`;
    } catch {
      const parts = source.split('/');
      return parts[parts.length - 1] || `File ${index + 1}`;
    }
  };

  const filteredContacts = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    const ordered = [...contacts].sort(
      (a, b) => Number(b.lastActivityAt || 0) - Number(a.lastActivityAt || 0),
    );
    if (!term) return ordered;
    return ordered.filter((contact) =>
      `${contact.name} ${contact.email} ${contact.jobTitle}`
        .toLowerCase()
        .includes(term),
    );
  }, [contacts, searchText]);

  const filteredGroups = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    const ordered = [...groupConversations].sort(
      (a, b) => Number(b.lastActivityAt || 0) - Number(a.lastActivityAt || 0),
    );
    if (!term) return ordered;
    return ordered.filter((group) =>
      `${group.title} ${group.preview}`.toLowerCase().includes(term),
    );
  }, [groupConversations, searchText]);

  const filteredAnnouncements = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return announcements;
    return announcements.filter((item) =>
      `${item.title || ''} ${item.message || ''} ${item.senderName || ''}`
        .toLowerCase()
        .includes(term),
    );
  }, [announcements, searchText]);

  const sharedFiles = useMemo(() => {
    const uniqueByUrl = new Map();

    messages.forEach((msg) => {
      const urls = Array.isArray(msg.attachmentUrls) ? msg.attachmentUrls : [];
      const isMine = msg.senderId && msg.senderId === currentUserIdRef.current;
      const senderName = isMine ? 'You' : selectedUser?.name || 'User';
      urls.forEach((url, index) => {
        if (!url || uniqueByUrl.has(url)) return;
        uniqueByUrl.set(url, {
          url,
          label: getAttachmentLabel(
            url,
            Array.isArray(msg.attachments) ? msg.attachments[index] : '',
            index,
          ),
          messageId: msg.id,
          senderName,
          createdAt: msg.createdAt,
        });
      });
    });

    return Array.from(uniqueByUrl.values()).reverse();
  }, [messages, selectedUser?.name]);

  const canCreateAnnouncement = useMemo(() => {
    if (!Array.isArray(currentUserRoles)) return false;
    return currentUserRoles.some((role) => {
      const normalized = String(role)
        .toLowerCase()
        .replace(/[\s_/-]+/g, '');
      return (
        normalized.includes('superadmin') ||
        normalized === 'admin' ||
        normalized.includes('operationsmanager') ||
        normalized.includes('manager')
      );
    });
  }, [currentUserRoles]);

  const canCreateGroup = useMemo(() => {
    if (!Array.isArray(currentUserRoles)) return false;
    return currentUserRoles.some((role) => {
      const normalized = String(role)
        .toLowerCase()
        .replace(/[\s_/-]+/g, '');
      return (
        normalized.includes('superadmin') ||
        normalized === 'admin' ||
        normalized.includes('operationsmanager') ||
        normalized.includes('manager') ||
        normalized.includes('supervisor')
      );
    });
  }, [currentUserRoles]);

  const userNameById = useMemo(() => {
    const map = new Map();
    allUsers.forEach((entry) => {
      if (!entry?.id) return;
      const name =
        `${entry.firstName || ''} ${entry.lastName || ''}`.trim() ||
        entry.email ||
        'User';
      map.set(entry.id, name);
    });
    return map;
  }, [allUsers]);

  const selectedParticipants = useMemo(() => {
    if (selectedGroup?.conversationId) {
      const summary = conversationSummaries.find(
        (item) => item.conversationId === selectedGroup.conversationId,
      );
      const members = Array.isArray(summary?.members) ? summary.members : [];
      return members.map((member) => ({
        id: member.id,
        name:
          member.id === currentUserIdRef.current
            ? 'You'
            : `${member.firstName || ''} ${member.lastName || ''}`.trim() ||
              member.email ||
              'User',
        subtitle: member.jobTitle || member.email || 'Member',
        roleLabel: getPrimaryRole(member.roles || []),
      }));
    }
    if (selectedUser?.id) {
      return [
        {
          id: selectedUser.id,
          name: selectedUser.name || 'User',
          subtitle: selectedUser.jobTitle || selectedUser.email || 'Member',
          roleLabel: getPrimaryRole(selectedUser.roles || []),
        },
      ];
    }
    return [];
  }, [conversationSummaries, selectedGroup, selectedUser]);

  const mentionCandidates = useMemo(() => {
    if (!selectedGroup?.conversationId) return [];
    const seen = new Set();
    return selectedParticipants
      .filter(
        (participant) =>
          participant?.id &&
          participant.id !== currentUserIdRef.current &&
          participant.name !== 'You',
      )
      .filter((participant) => {
        if (seen.has(participant.id)) return false;
        seen.add(participant.id);
        return true;
      })
      .map((participant) => ({
        id: participant.id,
        name: participant.name || 'User',
      }));
  }, [selectedGroup?.conversationId, selectedParticipants]);

  const filteredMentionCandidates = useMemo(() => {
    if (!showMentionPicker) return [];
    const term = mentionQuery.trim().toLowerCase();
    if (!term) return mentionCandidates;
    return mentionCandidates.filter((candidate) =>
      candidate.name.toLowerCase().includes(term),
    );
  }, [mentionCandidates, mentionQuery, showMentionPicker]);

  const selectedGroupMemberIds = useMemo(() => {
    if (!selectedGroup?.conversationId) return [];
    const summary = conversationSummaries.find(
      (item) => item.conversationId === selectedGroup.conversationId,
    );
    return Array.isArray(summary?.memberIds) ? summary.memberIds : [];
  }, [conversationSummaries, selectedGroup]);

  const availableUsersToAdd = useMemo(() => {
    if (!selectedGroup?.conversationId) return [];
    const blocked = new Set(selectedGroupMemberIds);
    return allUsers
      .filter((item) => item?.id && item.id !== currentUserIdRef.current)
      .filter((item) => !blocked.has(item.id))
      .map((item) => ({
        id: item.id,
        name: `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.email || 'User',
        roleLabel: getPrimaryRole(item.roles || []),
        roles: item.roles || [],
      }));
  }, [allUsers, selectedGroup?.conversationId, selectedGroupMemberIds]);

  const canManageSelectedGroup = Boolean(selectedGroup?.conversationId && canCreateGroup);

  useEffect(() => {
    const syncUnreadState = (event) => {
      const unreadCounts = getUnreadCounts();
      const activityMap = getChatActivityMap();
      const detail = event?.detail || {};
      setContacts((prev) => {
        const mapped = prev.map((contact) => ({
          ...contact,
          badge: Number(unreadCounts[contact.id] || 0),
          lastActivityAt: Number(
            activityMap[contact.id] || contact.lastActivityAt || 0,
          ),
        }));
        if (detail?.type === 'new-message' && detail?.senderId) {
          return mapped.sort((a, b) => {
            if (a.id === detail.senderId) return -1;
            if (b.id === detail.senderId) return 1;
            return (
              Number(b.lastActivityAt || 0) - Number(a.lastActivityAt || 0)
            );
          });
        }
        return mapped;
      });
    };

    syncUnreadState();
    window.addEventListener('admin-chat-unread-updated', syncUnreadState);
    return () =>
      window.removeEventListener('admin-chat-unread-updated', syncUnreadState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setActiveChatContact(selectedUser?.id || '');
    return () => {
      setActiveChatContact('');
    };
  }, [selectedUser]);

  useEffect(() => {
    if (leftPanelView === 'announcements') {
      markAnnouncementsAsRead();
    }
  }, [leftPanelView]);

  useEffect(() => {
    if (!isResizing) return undefined;

    const handleMouseMove = (event) => {
      const delta = event.clientX - resizeStartX.current;
      const nextWidth = Math.min(
        360,
        Math.max(210, resizeStartWidth.current + delta),
      );
      setLeftPaneWidth(nextWidth);
    };

    const handleMouseUp = () => setIsResizing(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleResizeStart = (event) => {
    resizeStartX.current = event.clientX;
    resizeStartWidth.current = leftPaneWidth;
    setIsResizing(true);
  };

  useEffect(() => {
    if (!token) {
      setError('Missing access token.');
      return;
    }
    try {
      const decoded = jwtDecode(token);
      currentUserIdRef.current = decoded?.sub?.userId || '';
    } catch {
      currentUserIdRef.current = '';
    }
  }, [token]);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const query = `
          query GetChatCandidates {
            getAllUser {
              data {
                id
                firstName
                lastName
                email
                roles
                jobTitle
                upload
              }
            }
          }
        `;
        const data = await graphqlRequest(query);
        const users = Array.isArray(data?.getAllUser?.data)
          ? data.getAllUser.data
          : [];
        setAllUsers(users.filter((user) => user?.id));
        const unreadCounts = getUnreadCounts();
        const activityMap = getChatActivityMap();
        const me = users.find((user) => user?.id === currentUserIdRef.current);
        setCurrentUserRoles(Array.isArray(me?.roles) ? me.roles : []);

        const toContact = (user) => ({
          id: user.id,
          name:
            `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
            'Unknown User',
          email: user.email || '',
          jobTitle: user.jobTitle || '',
          roles: user.roles || [],
          roleLabel: getPrimaryRole(user.roles || []),
          upload: user.upload || '',
          badge: Number(unreadCounts[user.id] || 0),
          lastActivityAt: Number(activityMap[user.id] || 0),
        });

        const mapped = users
          .filter((user) => user?.id && user.id !== currentUserIdRef.current)
          .filter((user) => isManagementRole(user.roles || []))
          .map(toContact)
          .sort(
            (a, b) =>
              Number(b.lastActivityAt || 0) - Number(a.lastActivityAt || 0),
          );

        setContacts(mapped);
      } catch (fetchError) {
        setError(fetchError?.message || 'Failed to load chat users');
      }
    };

    void fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentUserId = currentUserIdRef.current;
    if (!currentUserId) return;

    const summaries = Array.isArray(conversationSummaries)
      ? conversationSummaries
      : [];
    const rows = summaries
      .filter((item) => item?.type === 'GROUP')
      .map((item) => ({
        conversationId: item.conversationId,
        title: buildGroupTitle(item, currentUserId),
        preview: item.lastMessagePreview || 'No messages yet',
        memberCount: Array.isArray(item.memberIds) ? item.memberIds.length : 0,
        lastActivityAt: item.lastMessageAt
          ? new Date(item.lastMessageAt).getTime()
          : 0,
      }))
      .sort(
        (a, b) => Number(b.lastActivityAt || 0) - Number(a.lastActivityAt || 0),
      );
    setGroupConversations(rows);
  }, [conversationSummaries]);

  const loadMyConversations = useCallback(() => {
    if (!socketRef.current) return;
    socketRef.current.emit('getMyConversations', {}, (response = {}) => {
      const rows = Array.isArray(response?.conversations)
        ? response.conversations
        : [];
      setConversationSummaries(rows);
    });
  }, []);

  const applyAnnouncementRows = useCallback((rawRows) => {
    const serverRows = (Array.isArray(rawRows) ? rawRows : []).map(
      mapServerAnnouncement,
    );
    setAnnouncements((prev) => {
      const next = mergeAnnouncementLists(serverRows, prev);
      writeAnnouncements(next);
      return next;
    });
  }, []);

  const loadAnnouncements = useCallback(() => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit(
      'getAnnouncements',
      { limit: 50 },
      (response) => {
        // Only treat explicit Nest error status as failure — do not use
        // response.message (announcement payloads also have a message field
        // when the ack shape differs).
        if (response?.status === 'error') return;
        applyAnnouncementRows(extractAnnouncementsFromAck(response));
      },
    );
  }, [applyAnnouncementRows]);

  const openAnnouncement = useCallback(
    (item) => {
      if (!item) return;
      setSelectedUser(null);
      setSelectedGroup(null);
      setConversationId('');
      conversationIdRef.current = '';
      setMessages([]);
      setSelectedAnnouncement(item);
      setLeftPanelView('announcements');
      setActiveChatContact('');
      markAnnouncementsAsRead();
      const key = announcementKey(item);
      const next = new URLSearchParams(searchParams);
      if (!next.get('page')) next.set('page', 'chat');
      next.set('tab', 'announcements');
      if (key) next.set('announcementId', key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const openConversationById = useCallback((nextConversationId) => {
    if (!socketRef.current || !nextConversationId) return;
    setSelectedAnnouncement(null);
    setError('');
    setMessages([]);
    setConversationId(nextConversationId);
    conversationIdRef.current = nextConversationId;
    removeChatNotificationsForConversation(nextConversationId);
    socketRef.current.emit('joinConversation', {
      conversationId: nextConversationId,
    });
    socketRef.current.emit(
      'getMessages',
      { conversationId: nextConversationId, query: { limit: 50 } },
      (historyResponse = {}) => {
        const rows = Array.isArray(historyResponse.messages)
          ? [...historyResponse.messages].reverse()
          : [];
        setMessages(rows);
      },
    );
  }, []);

  const openDirectConversation = useCallback(
    (targetUser) => {
      if (!socketRef.current || !targetUser?.id) return;

      setError('');
      setMessages([]);
      setConversationId('');
      conversationIdRef.current = '';

      socketRef.current.emit(
        'resolveDirectConversation',
        { targetUserId: targetUser.id },
        (response = {}) => {
          if (!response?.conversationId) {
            setError('Unable to resolve direct conversation');
            return;
          }

          markContactAsRead(targetUser.id);
          openConversationById(response.conversationId);
          loadMyConversations();
        },
      );
    },
    [loadMyConversations, openConversationById],
  );

  const messageParticipantDirect = useCallback(
    (participant) => {
      if (!participant?.id || participant.id === currentUserIdRef.current) return;
      setSelectedGroup(null);
      openDirectConversation({
        id: participant.id,
        name: participant.name,
        email: participant.subtitle,
        jobTitle: participant.subtitle,
        roles: [],
      });
      setShowDetailsPanel(false);
    },
    [openDirectConversation],
  );

  useEffect(() => {
    if (!token) return undefined;

    const socket = createChatSocket(token);
    socketRef.current = socket;

    socket.on('connect', () => {
      setStatus('Connected');
      loadMyConversations();
      loadAnnouncements();
      if (selectedGroupRef.current?.conversationId) {
        openConversationById(selectedGroupRef.current.conversationId);
      } else if (selectedUserRef.current) {
        openDirectConversation(selectedUserRef.current);
      }
    });

    socket.on('disconnect', () => {
      setStatus('Disconnected');
    });

    socket.on('connect_error', (socketError) => {
      setStatus('Disconnected');
      setError(socketError?.message || 'Chat connection failed');
    });

    socket.on('message.created', (event) => {
      const activeContactId = selectedUserRef.current?.id || '';
      const isMine =
        event?.senderId && event.senderId === currentUserIdRef.current;
      const relatedContactId = isMine ? activeContactId : event?.senderId || '';
      const eventActivityTs = event?.createdAt
        ? new Date(event.createdAt).getTime()
        : Date.now();

      if (relatedContactId) {
        setChatActivity(relatedContactId, eventActivityTs);
        setContacts((prev) => {
          const updated = prev.map((item) =>
            item.id === relatedContactId
              ? {
                  ...item,
                  lastActivityAt: Math.max(
                    Number(item.lastActivityAt || 0),
                    Number(eventActivityTs),
                  ),
                }
              : item,
          );
          return updated.sort((a, b) => {
            if (a.id === relatedContactId) return -1;
            if (b.id === relatedContactId) return 1;
            return (
              Number(b.lastActivityAt || 0) - Number(a.lastActivityAt || 0)
            );
          });
        });
      }

      if (
        event?.conversationId !== conversationIdRef.current ||
        !conversationIdRef.current
      ) {
        return;
      }

      setMessages((prev) => {
        const exists = prev.some((item) => item.id === event.messageId);
        if (exists) return prev;
        return [
          ...prev,
          {
            id: event.messageId,
            senderId: event.senderId,
            senderName: event.senderName || '',
            senderType: event.senderType,
            content: event.content,
            attachments: event.attachmentKeys || [],
            attachmentUrls: event.attachmentUrls || [],
            mentionedUserIds: Array.isArray(event.mentionedUserIds)
              ? event.mentionedUserIds
              : [],
            createdAt: event.createdAt,
          },
        ];
      });
      loadMyConversations();
    });

    socket.on('notification.message', (event = {}) => {
      const isMine =
        event?.senderId && event.senderId === currentUserIdRef.current;
      if (isMine) {
        return;
      }

      const activeConversationId = conversationIdRef.current;
      const isActiveConversation =
        Boolean(activeConversationId) &&
        event?.conversationId === activeConversationId;
      const isMentioned = Array.isArray(event?.mentionedUserIds)
        ? event.mentionedUserIds.includes(currentUserIdRef.current)
        : false;

      pushChatNotification({
        eventId: event?.eventId,
        senderId: event?.senderId || '',
        senderName: event?.senderName || 'New message',
        content: isMentioned ? `@You ${event?.content || ''}` : event?.content || '',
        attachmentUrls: Array.isArray(event?.attachmentUrls)
          ? event.attachmentUrls
          : [],
        createdAt: event?.createdAt || new Date().toISOString(),
        conversationId: event?.conversationId || '',
        incrementUnread: !isActiveConversation,
      });

      loadMyConversations();
    });

    socket.on('conversation.created', () => {
      loadMyConversations();
    });
    socket.on('conversation.removed', (event = {}) => {
      if (!event?.conversationId) return;
      if (conversationIdRef.current === event.conversationId) {
        conversationIdRef.current = '';
        setConversationId('');
        setMessages([]);
        setSelectedGroup(null);
        setSelectedUser(null);
      }
      loadMyConversations();
    });

    socket.on('announcements.list', (payload = {}) => {
      applyAnnouncementRows(extractAnnouncementsFromAck(payload));
    });

    socket.on('announcement.created', (event = {}) => {
      const nextItem = mapServerAnnouncement(event);
      pushAnnouncementNotification({
        eventId: nextItem.eventId,
        title: nextItem.title,
        message: nextItem.message,
        senderName: nextItem.senderName,
        createdAt: nextItem.createdAt,
      });
      if (leftPanelViewRef.current === 'announcements') {
        markAnnouncementsAsRead();
      }

      setAnnouncements((prev) => {
        const next = mergeAnnouncementLists([nextItem], prev);
        writeAnnouncements(next);
        return next;
      });
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      conversationIdRef.current = '';
    };
  }, [
    applyAnnouncementRows,
    loadAnnouncements,
    loadMyConversations,
    openConversationById,
    openDirectConversation,
    token,
  ]);

  useEffect(() => {
    if (leftPanelView !== 'announcements') return;
    markAnnouncementsAsRead();
    loadAnnouncements();
  }, [leftPanelView, loadAnnouncements]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!showEmojiPicker) return undefined;

    const handleOutsideClick = (event) => {
      if (!emojiPickerRef.current) return;
      if (!emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showEmojiPicker]);

  useEffect(() => {
    if (!selectedGroup?.conversationId) {
      setShowMentionPicker(false);
      setMentionQuery('');
      setMentionStart(-1);
      setMentionActiveIndex(0);
    }
  }, [selectedGroup?.conversationId]);

  useEffect(() => {
    if (mentionActiveIndex >= filteredMentionCandidates.length) {
      setMentionActiveIndex(0);
    }
  }, [filteredMentionCandidates.length, mentionActiveIndex]);

  const handleSelectUser = useCallback(
    (user) => {
      setSelectedAnnouncement(null);
      setSelectedGroup(null);
      setSelectedUser(user);
      markContactAsRead(user.id);
      setContacts((prev) => {
        const updated = prev.map((item) =>
          item.id === user.id ? { ...item, badge: 0 } : item,
        );
        return updated.sort((a, b) => {
          if (a.id === user.id) return -1;
          if (b.id === user.id) return 1;
          return Number(b.lastActivityAt || 0) - Number(a.lastActivityAt || 0);
        });
      });
      if (socketRef.current?.connected) {
        openDirectConversation(user);
      }
    },
    [openDirectConversation],
  );

  const handleSelectGroup = useCallback(
    (group) => {
      setSelectedAnnouncement(null);
      setSelectedUser(null);
      setSelectedGroup(group);
      setPendingGroupMemberIds([]);
      if (socketRef.current?.connected && group?.conversationId) {
        openConversationById(group.conversationId);
      }
    },
    [openConversationById],
  );

  useEffect(() => {
    const requestedTab = (searchParams.get('tab') || '').toLowerCase();
    if (requestedTab === 'announcements') {
      setLeftPanelView('announcements');
      return;
    }
    if (requestedTab === 'groups') {
      setLeftPanelView('groups');
      return;
    }
    if (requestedTab === 'chats') {
      setLeftPanelView('chats');
    }
    // No default reset — preserves Announcements when URL has no tab=.
  }, [searchParams]);

  useEffect(() => {
    const requestedTab = (searchParams.get('tab') || '').toLowerCase();
    const requestedAnnouncementId = searchParams.get('announcementId') || '';
    if (requestedTab !== 'announcements' || !requestedAnnouncementId) return;
    if (
      handledDeepLinkRef.current === `announcement:${requestedAnnouncementId}`
    ) {
      return;
    }
    const match = announcements.find(
      (item) => announcementKey(item) === requestedAnnouncementId,
    );
    if (!match) return;
    handledDeepLinkRef.current = `announcement:${requestedAnnouncementId}`;
    openAnnouncement(match);
  }, [announcements, openAnnouncement, searchParams]);

  useEffect(() => {
    const requestedConversationId = searchParams.get('conversationId');
    if (!requestedConversationId) return;
    if (
      handledDeepLinkRef.current === `conversation:${requestedConversationId}`
    )
      return;

    const targetConversation = conversationSummaries.find(
      (item) => item?.conversationId === requestedConversationId,
    );

    handledDeepLinkRef.current = `conversation:${requestedConversationId}`;
    const myId = currentUserIdRef.current;

    if (!targetConversation) {
      setLeftPanelView('chats');
      setSelectedUser(null);
      setSelectedGroup({
        conversationId: requestedConversationId,
        title: 'Conversation',
        preview: '',
        memberCount: 0,
        lastActivityAt: Date.now(),
      });
      if (socketRef.current?.connected) {
        openConversationById(requestedConversationId);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('conversationId');
      setSearchParams(next);
      return;
    }

    if (targetConversation.type === 'GROUP') {
      setLeftPanelView('groups');
      setSelectedUser(null);
      setSelectedGroup({
        conversationId: targetConversation.conversationId,
        title: buildGroupTitle(targetConversation, myId),
        preview: targetConversation.lastMessagePreview || 'No messages yet',
        memberCount: Array.isArray(targetConversation.memberIds)
          ? targetConversation.memberIds.length
          : 0,
        lastActivityAt: targetConversation.lastMessageAt
          ? new Date(targetConversation.lastMessageAt).getTime()
          : 0,
      });
      if (socketRef.current?.connected) {
        openConversationById(targetConversation.conversationId);
      }
    } else {
      setLeftPanelView('chats');
      setSelectedGroup(null);
      const members = Array.isArray(targetConversation.members)
        ? targetConversation.members
        : [];
      const partner = members.find(
        (member) => member?.id && member.id !== myId,
      );
      if (partner) {
        const existingContact = contacts.find((item) => item.id === partner.id);
        const contact = existingContact || {
          id: partner.id,
          name:
            `${partner.firstName || ''} ${partner.lastName || ''}`.trim() ||
            partner.email ||
            'User',
          email: partner.email || '',
          jobTitle: partner.jobTitle || '',
          roles: Array.isArray(partner.roles) ? partner.roles : [],
          roleLabel: getPrimaryRole(partner.roles || []),
          upload: '',
          badge: 0,
          lastActivityAt: targetConversation.lastMessageAt
            ? new Date(targetConversation.lastMessageAt).getTime()
            : 0,
        };
        setSelectedUser(contact);
        markContactAsRead(contact.id);
      }
      if (socketRef.current?.connected) {
        openConversationById(targetConversation.conversationId);
      }
    }

    const next = new URLSearchParams(searchParams);
    next.delete('conversationId');
    setSearchParams(next);
  }, [
    contacts,
    conversationSummaries,
    openConversationById,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    const requestedContactId = searchParams.get('contactId');
    if (!requestedContactId) return;
    if (handledDeepLinkRef.current === `contact:${requestedContactId}`) return;
    const targetUser =
      contacts.find((item) => item.id === requestedContactId) ||
      allUsers
        .filter((item) => item?.id && item.id !== currentUserIdRef.current)
        .filter((item) => isManagementRole(item.roles || []))
        .map((item) => ({
          id: item.id,
          name:
            `${item.firstName || ''} ${item.lastName || ''}`.trim() ||
            item.email ||
            'User',
          email: item.email || '',
          jobTitle: item.jobTitle || '',
          roles: item.roles || [],
          roleLabel: getPrimaryRole(item.roles || []),
          upload: item.upload || '',
          badge: 0,
          lastActivityAt: 0,
        }))
        .find((item) => item.id === requestedContactId);
    if (!targetUser) {
      handledDeepLinkRef.current = `contact:${requestedContactId}`;
      openDirectConversation({
        id: requestedContactId,
        name: 'User',
        email: '',
        jobTitle: '',
        roles: [],
        roleLabel: '',
        upload: '',
        badge: 0,
        lastActivityAt: 0,
      });
      const next = new URLSearchParams(searchParams);
      next.delete('contactId');
      setSearchParams(next);
      return;
    }

    handledDeepLinkRef.current = `contact:${requestedContactId}`;
    handleSelectUser(targetUser);
    const next = new URLSearchParams(searchParams);
    next.delete('contactId');
    setSearchParams(next);
  }, [allUsers, contacts, handleSelectUser, openDirectConversation, searchParams, setSearchParams]);

  const generateUploadUrls = async (filesToUpload) => {
    const mutation = `
      mutation GenerateBookingFileUrls($files: [Fileupload!]!) {
        generateBookingFileUrlsForBooking(files: $files) {
          key
          url
        }
      }
    `;

    const data = await graphqlRequest(mutation, {
      files: filesToUpload.map((file) => ({
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
      })),
    });

    return data?.generateBookingFileUrlsForBooking || [];
  };

  const uploadFileToS3 = async (file, signedUrl) => {
    await axios.put(signedUrl, file);
  };

  const handleFileSelect = (event) => {
    const pickedFiles = Array.from(event.target.files || []);
    if (pickedFiles.length === 0) return;
    setSelectedFiles((prev) => [
      ...prev,
      ...toComposerFileItems(pickedFiles),
    ]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((prev) => {
      const item = prev[index];
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSend = async () => {
    const trimmedContent = content.trim();
    if (!trimmedContent && selectedFiles.length === 0) return;
    if (!conversationIdRef.current || !socketRef.current || isSending) return;

    const contentSnapshot = content;
    const filesSnapshot = selectedFiles;
    setContent('');
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setIsSending(true);
    setError('');
    try {
      let attachmentKeys = [];
      if (filesSnapshot.length > 0) {
        const files = filesSnapshot.map((item) => item.file);
        const uploadTargets = await generateUploadUrls(files);
        await Promise.all(
          uploadTargets.map((target, index) =>
            uploadFileToS3(files[index], target.url),
          ),
        );
        attachmentKeys = uploadTargets.map((target) => target.key);
      }

      await new Promise((resolve, reject) => {
        socketRef.current.emit(
          'sendMessage',
          {
            conversationId: conversationIdRef.current,
            content: trimmedContent,
            attachmentKeys,
          },
          (response = {}) => {
            const errMsg =
              response?.message ||
              (typeof response?.error === 'string' ? response.error : null);
            if (response?.messageId) {
              resolve(response);
              return;
            }
            reject(new Error(errMsg || 'Failed to send message'));
          },
        );
      });

      if (selectedUserRef.current?.id) {
        const selectedId = selectedUserRef.current.id;
        setChatActivity(selectedId, Date.now());
        setContacts((prev) => {
          const updated = prev.map((item) =>
            item.id === selectedId
              ? {
                  ...item,
                  lastActivityAt: Math.max(
                    Number(item.lastActivityAt || 0),
                    Date.now(),
                  ),
                }
              : item,
          );
          return updated.sort((a, b) => {
            if (a.id === selectedId) return -1;
            if (b.id === selectedId) return 1;
            return (
              Number(b.lastActivityAt || 0) - Number(a.lastActivityAt || 0)
            );
          });
        });
      }

      revokeComposerPreviews(filesSnapshot);
    } catch (sendError) {
      setContent(contentSnapshot);
      setSelectedFiles(filesSnapshot);
      setError(sendError?.message || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleEmojiSelect = (emojiData) => {
    setContent((prev) => `${prev}${emojiData?.emoji || ''}`);
  };

  const closeMentionPicker = () => {
    setShowMentionPicker(false);
    setMentionQuery('');
    setMentionStart(-1);
    setMentionActiveIndex(0);
  };

  const insertMention = useCallback(
    (candidate) => {
      const textarea = textareaRef.current;
      if (!textarea || mentionStart < 0 || !candidate?.name) {
        closeMentionPicker();
        return;
      }
      const cursor = textarea.selectionStart || 0;
      const before = content.slice(0, mentionStart);
      const after = content.slice(cursor);
      const mentionToken = `@${candidate.name} `;
      const nextContent = `${before}${mentionToken}${after}`;
      setContent(nextContent);
      closeMentionPicker();
      const caretPos = before.length + mentionToken.length;
      window.requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(caretPos, caretPos);
      });
    },
    [content, mentionStart],
  );

  const handleContentChange = (event) => {
    const nextValue = event.target.value;
    setContent(nextValue);

    if (!selectedGroup?.conversationId) {
      closeMentionPicker();
      return;
    }

    const cursor = event.target.selectionStart || 0;
    const textUntilCursor = nextValue.slice(0, cursor);
    const mentionMatch = textUntilCursor.match(/(^|\s)@([^\s@]*)$/);
    if (!mentionMatch) {
      closeMentionPicker();
      return;
    }

    const query = mentionMatch[2] || '';
    const start = cursor - query.length - 1;
    setMentionQuery(query);
    setMentionStart(start);
    setMentionActiveIndex(0);
    setShowMentionPicker(true);
  };

  const handleContentKeyDown = (event) => {
    if (!showMentionPicker || filteredMentionCandidates.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setMentionActiveIndex((prev) => (prev + 1) % filteredMentionCandidates.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setMentionActiveIndex((prev) =>
        prev === 0 ? filteredMentionCandidates.length - 1 : prev - 1,
      );
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      insertMention(filteredMentionCandidates[mentionActiveIndex]);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      closeMentionPicker();
    }
  };

  const focusMessageInThread = (messageId) => {
    if (!messageId) return;
    const target = messageRefs.current[messageId];
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedMessageId(messageId);
    window.setTimeout(() => setHighlightedMessageId(''), 1400);
  };

  const handleCreateAnnouncement = () => {
    if (!socketRef.current || !canCreateAnnouncement || isPostingAnnouncement)
      return;
    const title = announcementTitle.trim();
    const message = announcementMessage.trim();
    if (!title || !message) return;
    setIsPostingAnnouncement(true);
    setError('');
    socketRef.current.emit(
      'createAnnouncement',
      { title, message },
      (response = {}) => {
        if (response?.eventId) {
          setAnnouncementTitle('');
          setAnnouncementMessage('');
          openAnnouncement(
            mapServerAnnouncement({
              eventId: response.eventId,
              title,
              message,
              senderName: 'You',
              createdAt: new Date().toISOString(),
            }),
          );
        } else if (response?.message) {
          setError(response.message);
        }
        setIsPostingAnnouncement(false);
      },
    );
    window.setTimeout(() => setIsPostingAnnouncement(false), 2500);
  };

  const handleCreateGroup = () => {
    if (!socketRef.current || !canCreateGroup || isCreatingGroup) return;
    if (!socketRef.current.connected) {
      setError('Chat is not connected. Please wait and try again.');
      return;
    }
    if (!Array.isArray(groupMemberIds) || groupMemberIds.length === 0) {
      setError('Select at least one contact for the group');
      return;
    }
    setError('');
    setIsCreatingGroup(true);
    let settled = false;
    const finishCreating = () => {
      if (settled) return;
      settled = true;
      setIsCreatingGroup(false);
    };
    const timeoutId = window.setTimeout(() => {
      if (!settled) {
        setError('Creating group timed out. Please try again.');
        finishCreating();
      }
    }, 15000);
    socketRef.current.emit(
      'createGroupConversation',
      {
        memberIds: groupMemberIds,
        groupName: groupName.trim() || undefined,
      },
      (response = {}) => {
        window.clearTimeout(timeoutId);
        const errorMessage =
          response?.message ||
          (typeof response?.error === 'string' ? response.error : null);
        if (!response?.conversationId) {
          setError(errorMessage || 'Failed to create group');
          finishCreating();
          return;
        }
        setShowCreateGroup(false);
        setGroupMemberIds([]);
        setGroupName('');
        finishCreating();
        loadMyConversations();
        const created = {
          conversationId: response.conversationId,
          title: groupName.trim() || 'New Group',
          preview: 'No messages yet',
          memberCount: groupMemberIds.length + 1,
        };
        setSelectedUser(null);
        setSelectedGroup(created);
        openConversationById(response.conversationId);
      },
    );
  };

  const handleAddMembersToGroup = useCallback(() => {
    if (!socketRef.current || !selectedGroup?.conversationId || isAddingMembers) return;
    if (!Array.isArray(pendingGroupMemberIds) || pendingGroupMemberIds.length === 0) {
      setError('Select at least one member to add');
      return;
    }
    setError('');
    setIsAddingMembers(true);
    socketRef.current.emit(
      'addGroupMembers',
      {
        conversationId: selectedGroup.conversationId,
        memberIds: pendingGroupMemberIds,
      },
      (response = {}) => {
        if (!response?.conversationId) {
          setError(response?.message || 'Failed to add members');
          setIsAddingMembers(false);
          return;
        }
        setPendingGroupMemberIds([]);
        setIsAddingMembers(false);
        loadMyConversations();
      },
    );
    window.setTimeout(() => setIsAddingMembers(false), 2500);
  }, [isAddingMembers, loadMyConversations, pendingGroupMemberIds, selectedGroup?.conversationId]);

  const handleRemoveGroupMember = useCallback((memberId) => {
    if (!socketRef.current || !selectedGroup?.conversationId || !memberId || isRemovingMember) {
      return;
    }
    setError('');
    setIsRemovingMember(true);
    socketRef.current.emit(
      'removeGroupMember',
      {
        conversationId: selectedGroup.conversationId,
        memberId,
      },
      (response = {}) => {
        if (!response?.conversationId) {
          setError(response?.message || 'Failed to remove member');
        }
        setIsRemovingMember(false);
        loadMyConversations();
      },
    );
    window.setTimeout(() => setIsRemovingMember(false), 2500);
  }, [isRemovingMember, loadMyConversations, selectedGroup?.conversationId]);

  const handleLeaveGroup = useCallback(() => {
    if (!socketRef.current || !selectedGroup?.conversationId || isLeavingGroup) return;
    setError('');
    setIsLeavingGroup(true);
    socketRef.current.emit(
      'leaveGroupConversation',
      { conversationId: selectedGroup.conversationId },
      (response = {}) => {
        if (!response?.conversationId) {
          setError(response?.message || 'Failed to leave group');
          setIsLeavingGroup(false);
          return;
        }
        setIsLeavingGroup(false);
        setSelectedGroup(null);
        setConversationId('');
        conversationIdRef.current = '';
        setMessages([]);
        loadMyConversations();
      },
    );
    window.setTimeout(() => setIsLeavingGroup(false), 2500);
  }, [isLeavingGroup, loadMyConversations, selectedGroup?.conversationId]);

  const openRemoveMemberConfirm = (member) => {
    if (!member?.id) return;
    setGroupConfirmAction({
      type: 'remove',
      memberId: member.id,
      memberName: member.name || 'this member',
    });
  };

  const openLeaveGroupConfirm = () => {
    setGroupConfirmAction({ type: 'leave' });
  };

  const closeGroupConfirm = () => {
    setGroupConfirmAction(null);
  };

  const confirmGroupAction = useCallback(() => {
    if (!groupConfirmAction?.type) return;
    if (groupConfirmAction.type === 'remove' && groupConfirmAction.memberId) {
      handleRemoveGroupMember(groupConfirmAction.memberId);
    } else if (groupConfirmAction.type === 'leave') {
      handleLeaveGroup();
    }
    setGroupConfirmAction(null);
  }, [groupConfirmAction, handleLeaveGroup, handleRemoveGroupMember]);

  return (
    <section className="chat-dummy-page">
      <div
        className={`chat-dummy-shell ${showDetailsPanel ? 'details-open' : ''} ${
          isResizing ? 'is-resizing' : ''
        }`}
        style={{
          gridTemplateColumns: showDetailsPanel
            ? `${leftPaneWidth}px 6px 1fr 250px`
            : `${leftPaneWidth}px 6px 1fr`,
        }}
      >
        <aside className="chat-dummy-left">
          <div className="chat-dummy-left-top">
            <h2>Messages</h2>
          </div>
          <div className="chat-dummy-left-top-actions">
            <button
              type="button"
              className={`chat-dummy-groups-btn ${leftPanelView === 'chats' ? 'active' : ''}`}
              onClick={() => {
                setSelectedAnnouncement(null);
                setLeftPanelView('chats');
                const next = new URLSearchParams(searchParams);
                if (!next.get('page')) next.set('page', 'chat');
                next.set('tab', 'chats');
                next.delete('announcementId');
                setSearchParams(next, { replace: true });
              }}
            >
              <FaComments />
              Chats
            </button>
            <button
              type="button"
              className={`chat-dummy-groups-btn ${leftPanelView === 'groups' ? 'active' : ''}`}
              onClick={() => {
                setSelectedAnnouncement(null);
                setLeftPanelView('groups');
                const next = new URLSearchParams(searchParams);
                if (!next.get('page')) next.set('page', 'chat');
                next.set('tab', 'groups');
                next.delete('announcementId');
                setSearchParams(next, { replace: true });
              }}
            >
              <FaUsers />
              Groups
            </button>
            <button
              type="button"
              className={`chat-dummy-groups-btn ${
                leftPanelView === 'announcements' ? 'active' : ''
              }`}
              onClick={() => {
                setLeftPanelView('announcements');
                const next = new URLSearchParams(searchParams);
                if (!next.get('page')) next.set('page', 'chat');
                next.set('tab', 'announcements');
                setSearchParams(next, { replace: true });
              }}
            >
              <FaBullhorn />
              Announcements
            </button>
            {canCreateGroup ? (
              <button
                type="button"
                className="chat-dummy-create-group-btn"
                onClick={() => {
                  setLeftPanelView('groups');
                  setShowCreateGroup((prev) => !prev);
                }}
                title="Create group"
              >
                <FaPlus />
              </button>
            ) : null}
          </div>
          <div className="chat-dummy-search">
            <FaSearch />
            <input
              placeholder={
                leftPanelView === 'announcements'
                  ? 'Search announcements...'
                  : leftPanelView === 'groups'
                      ? 'Search groups...'
                      : 'Search conversations...'
              }
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          {leftPanelView === 'groups' ? (
            <>
              <p className="chat-dummy-group-title">All Groups</p>
              {showCreateGroup ? (
                <div className="chat-dummy-group-create-panel">
                  <input
                    type="text"
                    placeholder="Group name (optional)"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                  />
                  <p>Select contacts to add</p>
                  <div className="chat-dummy-group-members">
                    {allUsers
                      .filter((item) => item.id !== currentUserIdRef.current)
                      .map((item) => (
                        <label key={item.id} className="chat-dummy-check-row">
                          <input
                            type="checkbox"
                            checked={groupMemberIds.includes(item.id)}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setGroupMemberIds((prev) =>
                                checked
                                  ? [...new Set([...prev, item.id])]
                                  : prev.filter((id) => id !== item.id),
                              );
                            }}
                          />
                          <span>
                            {`${item.firstName || ''} ${item.lastName || ''}`.trim() ||
                              item.email}{' '}
                            ({getPrimaryRole(item.roles || [])})
                          </span>
                        </label>
                      ))}
                  </div>
                  <button
                    type="button"
                    onClick={handleCreateGroup}
                    disabled={isCreatingGroup}
                  >
                    {isCreatingGroup ? 'Creating...' : 'Create group'}
                  </button>
                </div>
              ) : null}
              {filteredGroups.map((group) => (
                <button
                  key={group.conversationId}
                  type="button"
                  className={`chat-dummy-item chat-dummy-item-button ${
                    selectedGroup?.conversationId === group.conversationId
                      ? 'active'
                      : ''
                  }`}
                  onClick={() => handleSelectGroup(group)}
                >
                  <div className="chat-dummy-avatar">
                    {group.title.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h4>{group.title}</h4>
                    <p>{group.preview}</p>
                  </div>
                  {group.memberCount > 0 ? (
                    <span className="chat-dummy-badge">
                      {group.memberCount}
                    </span>
                  ) : null}
                </button>
              ))}
            </>
          ) : leftPanelView === 'announcements' ? (
            <>
              <p className="chat-dummy-group-title">Announcements</p>
              {canCreateAnnouncement ? (
                <div className="chat-dummy-announcement chat-dummy-announcement-compose">
                  <input
                    type="text"
                    placeholder="Announcement title"
                    value={announcementTitle}
                    onChange={(e) => setAnnouncementTitle(e.target.value)}
                  />
                  <textarea
                    placeholder="Write announcement for all users..."
                    value={announcementMessage}
                    onChange={(e) => setAnnouncementMessage(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={handleCreateAnnouncement}
                    disabled={isPostingAnnouncement}
                  >
                    {isPostingAnnouncement ? 'Posting...' : 'Post announcement'}
                  </button>
                </div>
              ) : null}
              {filteredAnnouncements.length === 0 ? (
                <div className="chat-dummy-announcement">
                  <p>No announcements yet.</p>
                </div>
              ) : (
                filteredAnnouncements.map((item) => {
                  const key = announcementKey(item);
                  const isActive =
                    announcementKey(selectedAnnouncement) === key;
                  return (
                    <button
                      key={key || item.id}
                      type="button"
                      className={`chat-dummy-announcement chat-dummy-announcement-button ${
                        isActive ? 'active' : ''
                      }`}
                      onClick={() => openAnnouncement(item)}
                    >
                      <h3>{item.title}</h3>
                      <p>{item.message}</p>
                      <span>{item.senderName || 'Announcement'}</span>
                    </button>
                  );
                })
              )}
            </>
          ) : (
            <>
              <p className="chat-dummy-group-title">Direct Messages</p>
              {filteredContacts.map((chat) => (
                <button
                  key={chat.id}
                  type="button"
                  className={`chat-dummy-item chat-dummy-item-button ${
                    selectedUser?.id === chat.id ? 'active' : ''
                  }`}
                  onClick={() => handleSelectUser(chat)}
                >
                  <div className="chat-dummy-avatar">
                    {chat.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="chat-dummy-name-row">
                      <h4>{chat.name}</h4>
                      <span className="chat-dummy-role-chip">
                        {chat.roleLabel || getPrimaryRole(chat.roles) || 'User'}
                      </span>
                    </div>
                    <p>{chat.jobTitle || chat.email || 'User'}</p>
                  </div>
                  {chat.badge > 0 ? (
                    <span className="chat-dummy-badge">{chat.badge}</span>
                  ) : null}
                </button>
              ))}
            </>
          )}
        </aside>
        <div
          className={`chat-dummy-resizer ${isResizing ? 'active' : ''}`}
          onMouseDown={handleResizeStart}
          title="Drag to resize"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize messages panel"
        />

        <main className="chat-dummy-center">
          <header>
            <div className="chat-dummy-center-title">
              <h3>
                {selectedAnnouncement
                  ? selectedAnnouncement.title || 'Announcement'
                  : selectedUser?.name ||
                    selectedGroup?.title ||
                    'Select a chat to start'}
              </h3>
              <span>
                {selectedAnnouncement
                  ? selectedAnnouncement.senderName || 'Company announcement'
                  : selectedUser?.jobTitle ||
                    (selectedGroup ? 'Group chat' : status)}
              </span>
            </div>
            <button
              type="button"
              className="chat-dummy-dots-btn"
              onClick={() => setShowDetailsPanel((prev) => !prev)}
              title={showDetailsPanel ? 'Hide details' : 'Show details'}
            >
              <FaEllipsisV />
            </button>
          </header>

          {error ? <div className="chat-dummy-error">{error}</div> : null}

          {selectedAnnouncement ? (
            <div className="chat-dummy-message-wrap chat-dummy-announcement-detail-wrap">
              <article className="chat-dummy-announcement-detail">
                <h2>{selectedAnnouncement.title || 'Announcement'}</h2>
                <p className="chat-dummy-announcement-detail-meta">
                  {selectedAnnouncement.senderName || 'Announcement'}
                  {selectedAnnouncement.createdAt
                    ? ` · ${formatTime(selectedAnnouncement.createdAt)}`
                    : ''}
                </p>
                <p className="chat-dummy-announcement-detail-body">
                  {selectedAnnouncement.message || ''}
                </p>
              </article>
            </div>
          ) : (
          <div
            className="chat-dummy-message-wrap"
            ref={listRef}
            onScroll={(event) => {
              const container = event.currentTarget;
              const nodes = container.querySelectorAll('[data-chat-msg-date]');
              let label = '';
              for (const node of nodes) {
                const rect = node.getBoundingClientRect();
                const parent = container.getBoundingClientRect();
                if (rect.top >= parent.top - 8 && rect.top <= parent.bottom) {
                  label = node.getAttribute('data-chat-msg-date') || '';
                  break;
                }
              }
              if (label) setFloatingChatDate(label);
            }}
          >
            {floatingChatDate ? (
              <div className="chat-dummy-floating-date">{floatingChatDate}</div>
            ) : null}
            {messages.map((msg, index) => {
              const mine =
                msg.senderId && msg.senderId === currentUserIdRef.current;
              const mentionedMe =
                !mine &&
                Array.isArray(msg.mentionedUserIds) &&
                msg.mentionedUserIds.includes(currentUserIdRef.current);
              const senderLabel =
                msg.senderName ||
                userNameById.get(msg.senderId) ||
                (selectedGroup ? 'Group member' : selectedUser?.name || 'User');
              const prev = messages[index - 1];
              const showDateDivider =
                msg.createdAt &&
                (!prev?.createdAt ||
                  formatDateDividerLabel(prev.createdAt) !==
                    formatDateDividerLabel(msg.createdAt));
              return (
                <React.Fragment key={msg.id}>
                  {showDateDivider ? (
                    <div className="chat-dummy-date-divider">
                      <span>{formatDateDividerLabel(msg.createdAt)}</span>
                    </div>
                  ) : null}
                  <div
                    data-chat-msg-date={formatDateDividerLabel(msg.createdAt)}
                    className={`chat-dummy-bubble-row ${mine ? 'mine' : ''} ${
                      mentionedMe ? 'mentioned' : ''
                    } ${
                      highlightedMessageId === msg.id ? 'jump-highlight' : ''
                    }`}
                    ref={(el) => {
                      if (el) {
                        messageRefs.current[msg.id] = el;
                      }
                    }}
                  >
                    <div className={`chat-dummy-message ${mine ? 'mine' : ''}`}>
                      {selectedGroup && !mine ? (
                        <div className="chat-dummy-sender-name">
                          {senderLabel}
                        </div>
                      ) : null}
                      {mentionedMe ? (
                        <div className="chat-dummy-mention-chip">@You</div>
                      ) : null}
                      <div>{msg.content}</div>
                      {Array.isArray(msg.attachmentUrls) &&
                      msg.attachmentUrls.length > 0 ? (
                        <div className="chat-dummy-attachments">
                          {msg.attachmentUrls.map((url, fileIndex) => (
                            <a
                              key={`${msg.id}-file-${fileIndex}`}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {getAttachmentLabel(
                                url,
                                Array.isArray(msg.attachments)
                                  ? msg.attachments[fileIndex]
                                  : '',
                                fileIndex,
                              )}
                            </a>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <small>{formatMessageStamp(msg.createdAt)}</small>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
          )}

          {!selectedAnnouncement ? (
          <footer className="chat-dummy-input">
            {showMentionPicker ? (
              <div className="chat-dummy-mention-picker">
                {filteredMentionCandidates.length === 0 ? (
                  <div className="chat-dummy-mention-empty">No matching members</div>
                ) : (
                  filteredMentionCandidates.map((candidate, index) => (
                    <button
                      key={candidate.id}
                      type="button"
                      className={index === mentionActiveIndex ? 'active' : ''}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        insertMention(candidate);
                      }}
                    >
                      @{candidate.name}
                    </button>
                  ))
                )}
              </div>
            ) : null}
            {selectedFiles.length > 0 ? (
              <div className="chat-dummy-attachment-preview">
                {selectedFiles.map((item, index) => (
                  <div
                    key={`${item.file?.name || 'file'}-${index}`}
                    className="chat-dummy-preview-card"
                  >
                    {item.previewUrl ? (
                      <img src={item.previewUrl} alt={item.file?.name || 'Attachment'} />
                    ) : (
                      <div className="chat-dummy-preview-file">
                        <FaPaperclip />
                        <span>{item.file?.name || 'File'}</span>
                      </div>
                    )}
                    <button
                      type="button"
                      className="chat-dummy-preview-remove"
                      aria-label="Remove attachment"
                      onClick={() => removeSelectedFile(index)}
                    >
                      <FaTimes size={12} />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
            <div className="chat-dummy-compose-row">
              <textarea
                ref={textareaRef}
                placeholder={
                  selectedFiles.length > 0
                    ? 'Add a caption…'
                    : 'Type your message...'
                }
                value={content}
                onChange={handleContentChange}
                onKeyDown={handleContentKeyDown}
              />
              <div className="chat-dummy-compose-tools">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={handleFileSelect}
                />
                <button
                  type="button"
                  className="chat-dummy-tool-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!conversationId}
                  aria-label="Attach file"
                >
                  <FaPaperclip />
                </button>
                <button
                  type="button"
                  className="chat-dummy-tool-btn"
                  onClick={() => setShowEmojiPicker((prev) => !prev)}
                  disabled={!conversationId}
                  aria-label="Emoji"
                >
                  <FaRegSmile />
                </button>
                <button
                  type="button"
                  className="chat-dummy-send"
                  onClick={handleSend}
                  disabled={
                    !conversationId ||
                    isSending ||
                    (!content.trim() && selectedFiles.length === 0)
                  }
                  aria-label="Send message"
                >
                  <FaTelegramPlane size={18} />
                </button>
              </div>
            </div>
            {showEmojiPicker ? (
              <div className="chat-dummy-emoji-picker" ref={emojiPickerRef}>
                <EmojiPicker
                  onEmojiClick={handleEmojiSelect}
                  className="chat-emoji-library-picker"
                  width="100%"
                  height={340}
                  autoFocusSearch={false}
                  lazyLoadEmojis
                  previewConfig={{ showPreview: false }}
                />
              </div>
            ) : null}
          </footer>
          ) : null}
        </main>

        <aside className={`chat-dummy-right ${showDetailsPanel ? 'open' : ''}`}>
          <div className="chat-dummy-right-head">
            <div>
              <h3>Conversation Details</h3>
              <p>View information and shared files</p>
            </div>
            <button
              type="button"
              className="chat-dummy-close-details"
              onClick={() => setShowDetailsPanel(false)}
              title="Close details"
            >
              <FaTimes />
            </button>
          </div>

          <div className="chat-dummy-panel">
            <h4>Participants ({selectedParticipants.length})</h4>
            {selectedParticipants.length === 0 ? (
              <p>No user selected</p>
            ) : (
              <div className="chat-dummy-links-list">
                {selectedParticipants.map((participant) => (
                  <div key={participant.id} className="chat-dummy-user">
                    <div className="chat-dummy-avatar">
                      {participant.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <strong>
                        {participant.name}
                        {participant.roleLabel ? ` (${participant.roleLabel})` : ''}
                      </strong>
                      <p>{participant.subtitle}</p>
                    </div>
                    <div className="chat-dummy-participant-actions">
                      {participant.id !== currentUserIdRef.current ? (
                        <button
                          type="button"
                          className="chat-dummy-member-action"
                          onClick={() => messageParticipantDirect(participant)}
                        >
                          Message
                        </button>
                      ) : null}
                      {canManageSelectedGroup &&
                      participant.id !== currentUserIdRef.current ? (
                        <button
                          type="button"
                          className="chat-dummy-member-action danger"
                          onClick={() => openRemoveMemberConfirm(participant)}
                          disabled={isRemovingMember}
                        >
                          {isRemovingMember ? 'Removing...' : 'Remove'}
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {selectedGroup && canManageSelectedGroup ? (
              <div className="chat-dummy-group-create-panel chat-dummy-add-members-panel">
                <p>Add members to this group</p>
                <div className="chat-dummy-group-members">
                  {availableUsersToAdd.length === 0 ? (
                    <p className="chat-dummy-muted">All available users are already in this group.</p>
                  ) : (
                    availableUsersToAdd.map((contact) => (
                      <label key={contact.id} className="chat-dummy-check-row">
                        <input
                          type="checkbox"
                          checked={pendingGroupMemberIds.includes(contact.id)}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setPendingGroupMemberIds((prev) =>
                              checked
                                ? [...new Set([...prev, contact.id])]
                                : prev.filter((id) => id !== contact.id),
                            );
                          }}
                        />
                        <span>
                          {contact.name} ({contact.roleLabel || getPrimaryRole(contact.roles || [])})
                        </span>
                      </label>
                    ))
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleAddMembersToGroup}
                  disabled={isAddingMembers || availableUsersToAdd.length === 0}
                >
                  {isAddingMembers ? 'Adding...' : 'Add members'}
                </button>
              </div>
            ) : null}
            {selectedGroup?.conversationId ? (
              <button
                type="button"
                className="chat-dummy-member-action leave"
                onClick={openLeaveGroupConfirm}
                disabled={isLeavingGroup}
              >
                {isLeavingGroup ? 'Leaving...' : 'Leave group'}
              </button>
            ) : null}
          </div>

          <div className="chat-dummy-panel">
            <h4>Shared Files ({sharedFiles.length})</h4>
            {sharedFiles.length === 0 ? (
              <p>No files shared yet</p>
            ) : (
              <div className="chat-dummy-links-list">
                {sharedFiles.map((file) => (
                  <div
                    key={file.url}
                    className="chat-dummy-file-link"
                    onClick={() => focusMessageInThread(file.messageId)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        focusMessageInThread(file.messageId);
                      }
                    }}
                  >
                    <strong>{file.label}</strong>
                    <small>
                      Sent by {file.senderName} • {formatTime(file.createdAt)}
                    </small>
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => event.stopPropagation()}
                    >
                      Open file
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>
      {groupConfirmAction ? (
        <div className="chat-dummy-confirm-overlay">
          <div className="chat-dummy-confirm-modal">
            <h4>
              {groupConfirmAction.type === 'remove'
                ? 'Remove member from group?'
                : 'Leave this group?'}
            </h4>
            <p>
              {groupConfirmAction.type === 'remove'
                ? `This will remove ${groupConfirmAction.memberName} from the group.`
                : 'You will be removed from this group conversation.'}
            </p>
            <div className="chat-dummy-confirm-actions">
              <button type="button" onClick={closeGroupConfirm}>
                Cancel
              </button>
              <button
                type="button"
                className="danger"
                onClick={confirmGroupAction}
              >
                {groupConfirmAction.type === 'remove' ? 'Remove' : 'Leave'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ChatWorkspace;
